import {
  Badge,
  Button,
  Checkbox,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useState } from "react";

export interface FilterOption {
  label: string;
  value: string;
  tooltip?: string;
}

interface FilterSelectProps {
  label: string;
  startIcon: React.ReactNode;
  options: FilterOption[];
  selectedOptions: string[];
  setSelectedOptions: (selectedOptions: string[]) => void;
  defaultUnchecked?: string[];
  hideTextWhenSmall?: boolean;
}

const FilterSelect = ({
  label,
  startIcon,
  options,
  selectedOptions,
  setSelectedOptions,
  hideTextWhenSmall = false,
}: FilterSelectProps) => {
  const [open, setOpen] = useState(false);
  const filters = new Set(selectedOptions);
  const badgeCount = selectedOptions.length;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();

  const isLgOrBelow = useMediaQuery(theme.breakpoints.down("lg"));

  const handleClickMenuItem = (v: string) => {
    const newFilters = new Set(filters);
    if (filters.has(v)) {
      newFilters.delete(v);
    } else {
      newFilters.add(v);
    }
    setSelectedOptions([...newFilters.keys()]);
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setOpen(false);
  };
  return (
    <>
      {isLgOrBelow && hideTextWhenSmall ? (
        <IconButton
          onClick={handleOpenMenu}
          data-testid={`filter-select-${label}`}
        >
          <Badge badgeContent={badgeCount} color="secondary">
            {startIcon}
          </Badge>
        </IconButton>
      ) : (
        <Button
          onClick={handleOpenMenu}
          startIcon={startIcon}
          sx={{
            paddingRight: badgeCount ? 2 : 0,
          }}
          data-testid={`filter-select-${label}`}
        >
          <Stack
            direction="row"
            sx={{
              gap: 2,
              alignItems: "center",
            }}
          >
            {label}
            <Badge badgeContent={badgeCount} color="secondary" />
          </Stack>
        </Button>
      )}
      <Menu anchorEl={anchorEl} open={open} onClose={handleCloseMenu}>
        <Stack
          sx={{
            overflow: "auto",
            maxHeight: 400,
          }}
        >
          {options.map((option: FilterOption) => (
            <MenuItem
              key={option?.value}
              onClick={() => handleClickMenuItem(option?.value)}
            >
              <Checkbox checked={filters.has(option?.value)} />
              <Tooltip title={option?.tooltip} arrow>
                <ListItemText primary={option?.label} />
              </Tooltip>
            </MenuItem>
          ))}
        </Stack>
      </Menu>
    </>
  );
};

export default FilterSelect;
